import { useCallback } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import { Box } from './StyledComponents';
import { useDeliverForm } from './useDeliverForm.jsx';
import Select from '../../../../shared/Select';
import Text from '../../../../shared/Text';
import TextInput from '../../../../shared/FormStyles/TextInput.jsx';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import ModalConfirmation from '../../../../shared/ModalConfirmation';
import Modal from '../../../../shared/Modal';

const Wrapper = styled(Box)`
  background-color: ${themeGet('colors.white')};
  padding: 24px;
`;

const RecipientsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const RecipientsTileWrapper = styled(RecipientsWrapper)`
  margin: 0 0 8px;
`;

const FieldWrapper = styled.div`
  margin: 0 0 24px;

  &:last-of-type {
    margin: 0;
  }
`;

const Button = styled.button`
  border-style: solid;
  border-width: 1px;
  height: 40px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;

  &:disabled {
    background: ${themeGet('colors.black.5')};
    color: ${themeGet('colors.black.60')};
    border-color: ${themeGet('colors.black.20')};

    &:hover {
      color: ${themeGet('colors.black.60')};
      background: ${themeGet('colors.black.5')};
      border-color: ${themeGet('colors.black.20')};
    }

    &:focus {
      background: ${themeGet('colors.black.5')};
      color: ${themeGet('colors.black.60')};
      border-color: ${themeGet('colors.black.20')};
    }
  }
`;

const SaveButton = styled(Button)`
  color: ${themeGet('colors.black.80')};
  border-color: ${themeGet('colors.black.60')};
  background: white;
  margin: 24px 0 0;

  &:hover {
    color: ${themeGet('colors.black.80')};
    border-color: ${themeGet('colors.black.60')};
    background: white;
  }

  &:focus {
    color: ${themeGet('colors.black.80')};
    border-color: ${themeGet('colors.black.60')};
    background: white;
  }
`;

const SendTestButton = styled(Button)`
  color: ${themeGet('colors.primary.100')};
  border-color: ${themeGet('colors.primary.100')};
  background: white;
  margin: 16px 0;

  &:hover {
    color: ${themeGet('colors.primary.100')};
    border-color: ${themeGet('colors.primary.100')};
    background: white;
  }

  &:focus {
    color: ${themeGet('colors.primary.100')};
    border-color: ${themeGet('colors.primary.100')};
    background: white;
  }
`;

const SendButton = styled(Button)`
  background: ${themeGet('colors.primary.100')};
  border-color: ${themeGet('colors.primary.100')};
  color: white;

  &:hover {
    background: ${themeGet('colors.primary.100')};
    border-color: ${themeGet('colors.primary.100')};
    color: white;
  }

  &:focus {
    background: ${themeGet('colors.primary.100')};
    border-color: ${themeGet('colors.primary.100')};
    color: white;
  }

  margin: 0;
`;

const RecipientsTile = styled.span`
  font-size: 14px;
  font-weight: 400px;
  color: ${themeGet('colors.black.80')};
`;

const EmailRecipients = styled.span`
  color: ${themeGet('colors.black.100')};
  font-size: 18px;
  font-weight: 500;
`;

const SmsRecipients = styled.span`
  color: ${themeGet('colors.black.80')};
  font-size: 18px;
  font-weight: 500;
`;

export default function DeliverForm() {
  const {
    handleChange,
    values,
    deliver,
    setFieldValue,
    onSave,
    onSend,
    onSendTest,
    disabledSend,
    disabledSendTest,
    disabledSave,
  } = useDeliverForm();
  const { t } = useTranslation('send');
  const { timezone } = useSelector(state => state.currentBoard);

  const getRecipientsCount = type => {
    const { audiencePreview, audienceCounts } = values;

    if (audiencePreview)
      return audienceCounts[audiencePreview][`${type}_count`];

    return values.digestSendTo === 'true'
      ? deliver[`all_user_${type}_recipient_count`]
      : deliver[`activated_user_${type}_recipient_count`];
  };

  const emailRecipientsCount = getRecipientsCount('email');
  const smsRecipientsCount = getRecipientsCount('sms');

  const confirmOnSend = useCallback(() => {
    const currentHour = () =>
      formatInTimeZone(new Date(), timezone, 'hh:mmaaa zzz');

    Modal({
      component: (
        <ModalConfirmation
          onSave={() => {
            onSend();
            Swal.close();
          }}
          header={t('deliver.confirmHeader')}
          body={t('deliver.confirmMsg', { time: currentHour() })}
          submitText={t('deliver.confirmSend')}
          cancelText={t('deliver.confirmCancel')}
        />
      ),
    });
  }, [onSend, t, timezone]);

  return (
    <Wrapper>
      <FieldWrapper>
        <RecipientsTileWrapper>
          <RecipientsTile>{t('deliver.emailRecipients')}</RecipientsTile>
          <RecipientsTile>{t('deliver.smsRecipients')}</RecipientsTile>
        </RecipientsTileWrapper>
        <RecipientsWrapper>
          <EmailRecipients>{emailRecipientsCount}</EmailRecipients>
          <SmsRecipients>{smsRecipientsCount}</SmsRecipients>
        </RecipientsWrapper>
      </FieldWrapper>
      <FieldWrapper>
        <Select
          components={{
            IndicatorSeparator: () => null,
          }}
          onChange={option => setFieldValue('audiencePreview', option.value)}
          options={deliver.population_segments_options}
          value={deliver.population_segments_options.find(
            item => item.value === values.audiencePreview,
          )}
          isSearchable={false}
          name="audiencePreview"
          label={t('deliver.form.audiencePreview')}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Select
          components={{
            IndicatorSeparator: () => null,
          }}
          isSearchable={false}
          onChange={option => setFieldValue('digestSendTo', option.value)}
          options={deliver.digest_send_to_options}
          value={deliver.digest_send_to_options.find(
            item => item.value === values.digestSendTo,
          )}
          name="digestSendTo"
          label={t('deliver.form.digestSendTo')}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Select
          components={{
            IndicatorSeparator: () => null,
          }}
          onChange={option => setFieldValue('followUpDay', option.value)}
          options={deliver.follow_up_day_options.map(day => ({
            value: day,
            label: day,
          }))}
          value={{ value: values.followUpDay, label: values.followUpDay }}
          isSearchable={false}
          name="followUpDay"
          label={t('deliver.form.followUpDay')}
        />
      </FieldWrapper>
      <FieldWrapper>
        <RecipientsWrapper>
          <Text variant="body2" color="black.80" as="span">
            {t('deliver.form.includeSms')}
          </Text>
          <ToggleSwitch
            id="includeSms"
            onChange={handleChange}
            value={values.includeSms}
            name="includeSms"
          />
        </RecipientsWrapper>
      </FieldWrapper>
      <FieldWrapper>
        <TextInput
          onChange={handleChange}
          value={values.customSubject}
          placeholder={t('deliver.form.customSubject.placeholder')}
          name="customSubject"
          label={t('deliver.form.customSubject.label')}
          charactersLimit={80}
        />
      </FieldWrapper>
      <FieldWrapper>
        <TextInput
          onChange={handleChange}
          value={values.altCustomSubject}
          placeholder={t('deliver.form.altCustomSubject.placeholder')}
          name="altCustomSubject"
          label={t('deliver.form.altCustomSubject.label')}
          charactersLimit={80}
        />
      </FieldWrapper>
      <FieldWrapper>
        <TextInput
          onChange={handleChange}
          value={values.customHeadline}
          placeholder={t('deliver.form.customHeadline.placeholder')}
          name="customHeadline"
          label={t('deliver.form.customHeadline.label')}
          charactersLimit={80}
        />
      </FieldWrapper>
      <FieldWrapper>
        <TextInput
          onChange={handleChange}
          value={values.customMessage}
          placeholder={t('deliver.form.customMessage.placeholder')}
          name="customMessage"
          label={t('deliver.form.customMessage.label')}
          charactersLimit={160}
          as="textarea"
        />
      </FieldWrapper>
      <SaveButton disabled={disabledSave} onClick={onSave}>
        {t('deliver.form.save.button')}
      </SaveButton>
      <SendTestButton disabled={disabledSendTest} onClick={onSendTest}>
        {t('deliver.form.sendTest.button')}
      </SendTestButton>
      <SendButton disabled={disabledSend} onClick={confirmOnSend}>
        {t('deliver.form.send.button')}
      </SendButton>
    </Wrapper>
  );
}
